import React from 'react';

import { useTranslator } from '../../../components/i18n/useTranslator';
import TaskAssignmentPopupContent from '../../task/pills/assignment/TaskAssignmentPopupContent';
import ToggleChip from '../behaviours/ToggleChip';
import AssignmentChipView, { type RenderAssignmentPopupContent } from './AssignmentChipView';
import type { ElementAssignmentPlaceholderChipDescriptor } from '../elementChipsTypes';
import { ChipBaseProps } from '../../../../common/@roma/src/components/base/chip/ChipBase';

type AssignmentPlaceholderChipProps = {
    chip: ElementAssignmentPlaceholderChipDescriptor;
    size: ChipBaseProps['size'];
    canModify: boolean;
    /** Removes the (empty) assignment slot from the owning container. */
    onRemove: () => void;
    /** Renders the assignee picker shown in the chip's selection-owned popup. Defaults to the element-id popup. */
    renderPopupContent?: RenderAssignmentPopupContent;
};

/**
 * Renders an assignment placeholder pill and its generic selection-owned picker view.
 */
const AssignmentPlaceholderChip: React.FC<AssignmentPlaceholderChipProps> = ({
    chip,
    size,
    canModify,
    onRemove,
    renderPopupContent,
}) => {
    const translate = useTranslator('elements', 'elementTypes.task');
    const { containerId, isSearchHighlighted } = chip;
    const popupId = `task-assignment-${containerId}-0`;

    const renderContent: RenderAssignmentPopupContent =
        renderPopupContent ?? (() => <TaskAssignmentPopupContent popupId={popupId} elementId={containerId} />);

    return (
        <ToggleChip chip={chip} canModify={canModify} onDelete={onRemove}>
            {({ isSelected, canActivate, deselect, chipRef, chipProps }) => (
                <AssignmentChipView
                    chipRef={chipRef}
                    chipProps={chipProps}
                    size={size}
                    variant="normal"
                    containerId={containerId}
                    isSelected={isSelected}
                    isDisabled={!canActivate}
                    isSearchHighlighted={isSearchHighlighted}
                    deselect={deselect}
                    renderPopupContent={renderContent}
                >
                    {translate('assignment.placeholder')}
                </AssignmentChipView>
            )}
        </ToggleChip>
    );
};

export default AssignmentPlaceholderChip;
